import { GeApi, GeApiState, GlobalServiceInterface } from "../Ge";
import {
    AllRecipientTypes,
    AuditActor,
    AuditEmailRecipient,
    AuditEvent,
    AuditingEmailData,
    AuditPayload,
    PDPDecision,
    PlatformInfo,
    RecipientsByAllTypes,
    UserDecision
} from "../Ge/types";
import { Container, GlobalConfiguration } from "./GlobalConfiguration";
import { OutlookActions, RecipientType } from "./OutlookActions";
import { GeException } from "../infrastructure"
import { AppointMentRecipientType } from "./AppointmentOutlookActions";
import { v4 as uuidv4 } from 'uuid';
import { i18n, TFunction } from "i18next";
import { DECISION_OVERRIDE, GRAPH_ENDPOINT } from "./constants";

export interface ResolvedEmails {
    recipientsByType: RecipientsByAllTypes;
    unresolvedLists: string[];
}

export class EmailFields {

    protected globalService: GlobalServiceInterface;
    protected container: Container;
    protected itemType: Office.MailboxEnums.ItemType;
    private _unresolvedLists: string[] = [];

    constructor(globalService: GlobalServiceInterface, container: Container, itemType: Office.MailboxEnums.ItemType) {
        this.globalService = globalService;
        this.container = container;
        this.itemType = itemType;
    }

    private async resolveEmailList(recipientField: AllRecipientTypes): Promise<string[]> {
        let recipientList: Set<string> = new Set();

        const toRecipients = await this.globalService.outlookActions.getRecipients(recipientField);
        for (let idx = 0; idx < toRecipients.length; idx++) {
            const recipient = toRecipients[idx];
            const emailAddress = recipient.emailAddress;

            switch (recipient.recipientType) {
                case Office.MailboxEnums.RecipientType.Other:
                case Office.MailboxEnums.RecipientType.DistributionList: {
                    const dlMembers = await this.resolveDistributionList(emailAddress, recipient.displayName, toRecipients);
                    dlMembers?.forEach((dlMember) => recipientList.add(dlMember));
                } break;

                case Office.MailboxEnums.RecipientType.User:
                case Office.MailboxEnums.RecipientType.ExternalUser:
                default:

                    if (!emailAddress) {
                        this.globalService.apm.setCustomContext({
                            recipientField,
                            recipient,
                            "recipient-not-resolved": true
                        });

                        continue;
                    }

                    this.checkEmailAddressFormat(emailAddress, toRecipients);
                    recipientList.add(emailAddress.toLowerCase());
                    break;
            }
        }
        return Array.from(recipientList);
    }

    /**
     * This function expands a Distribution List. It can handle nested lists, but it doesn't check for duplicate records
     * @param dlEmail the email address of the list
     */
        private async resolveDistributionList(dlEmail: string, dlName: string, recipientField?: Office.EmailAddressDetails[]): Promise<string[]> {
            let recipientList: string[] = [];

            // If there's no email address, this is a personal contact list — skip Graph lookup
            // to avoid accidentally matching an unrelated Azure AD group with the same display name.
            // Only treat strictly empty/blank dlEmail as a CL; non-SMTP identifiers (e.g. legacyExchangeDN)
            // should still attempt Graph resolution.
            if (!dlEmail || !dlEmail.trim()) {
                if (dlName) {
                    this._unresolvedLists.push(dlName);

                    this.globalService?.apm?.setCustomContext({
                        recipientField,
                        name: dlName,
                        "recipient-not-resolved": true,
                        "reason": "Personal contact list detected (no SMTP address) — sent as unresolved list"
                    });
                }
                return recipientList;
            }

            const dlMembers = await this.globalService.outlookActions.resolveGraphDL(dlName, dlEmail);
    
            if (dlMembers && dlMembers.length) {
                for (let idx in dlMembers) {
                    const expandedDlRecipient = dlMembers[idx];
                    switch (expandedDlRecipient["@odata.type"]) {
                        case "#microsoft.graph.user":
                            if (expandedDlRecipient.mail) {
                                recipientList.push(expandedDlRecipient.mail.toLowerCase());
                            } else {
                                this.globalService?.apm?.setCustomContext({
                                    recipientField,
                                    name: dlName,
                                    "recipient-not-resolved": true,
                                    "reason": `Could not resolve email for user member in group, recipient removed`
                                });
                            }
                            break;
                        case "#microsoft.graph.group":
                            if (expandedDlRecipient.mail) {
                                const nestedList = await this.resolveDistributionList(expandedDlRecipient.mail, null, recipientField);
                                recipientList = recipientList.concat(nestedList);
                            } else if (expandedDlRecipient.id) {
                                // Nested group with no mail (e.g., on-prem synced) — expand recursively by id
                                try {
                                    const expanded = await this.expandGroupById(expandedDlRecipient.id, recipientField);
                                    recipientList = recipientList.concat(expanded);
                                } catch (e) {
                                    this.globalService?.apm?.captureError?.(e instanceof Error ? e : new Error(String(e)));
                                }
                            }
                            break;
                        case "#microsoft.graph.orgContact":
                        case "#microsoft.graph.device":
                        case "#microsoft.graph.servicePrincipal":
                        default:
                            if (expandedDlRecipient.mail) {
                                recipientList.push(expandedDlRecipient.mail.toLowerCase());
                            } else {
                                // transaction skipped recipient without email in group
                                this.globalService?.apm?.setCustomContext({
                                    recipientField,
                                    name: dlName,
                                    "recipient-not-resolved": true,
                                    "reason": `Could not resolve email for member in contact list of type ${expandedDlRecipient["@odata.type"]}, recipient removed`
                                });
                            }
                    }
                };
            }
            else {
                // Graph couldn't find a matching group.
                if (dlEmail.includes('@')) {
                    // Has valid SMTP format — likely a Dynamic Distribution Group or org DL
                    // not synced to Azure AD. Pass through to server validation.
                    this.checkEmailAddressFormat(dlEmail, recipientField);
                    recipientList.push(dlEmail.toLowerCase());
                } else {
                    // Non-SMTP identifier (e.g. legacyExchangeDN) that Graph couldn't resolve.
                    // Treat as unresolved so backend can block with no override.
                    const name = dlName || dlEmail;
                    this._unresolvedLists.push(name);
                    this.globalService?.apm?.setCustomContext({
                        recipientField,
                        name,
                        "recipient-not-resolved": true,
                        "reason": "Non-SMTP identifier could not be resolved via Graph — sent as unresolved list"
                    });
                }
            }    
            return recipientList;
        }

    /**
     * Recursively expands a group by its Azure AD id, resolving all leaf members.
     * Handles nested groups at any depth (with mail or id-only).
     * @param groupId - Azure AD group id
     * @param recipientField - for APM context
     * @param depth - recursion guard (max 10 levels)
     */
    private async expandGroupById(groupId: string, recipientField?: Office.EmailAddressDetails[], depth: number = 0): Promise<string[]> {
        if (depth > 10) return []; // guard against infinite recursion

        const members = await this.globalService.outlookActions.makeGraphRequest(
            `${GRAPH_ENDPOINT}/v1.0/groups/${groupId}/members?$select=id,mail`
        );

        let recipientList: string[] = [];
        for (const member of members.data.value) {
            if (member["@odata.type"] === "#microsoft.graph.group") {
                if (member.mail) {
                    const innerList = await this.resolveDistributionList(member.mail, null, recipientField);
                    recipientList = recipientList.concat(innerList);
                } else if (member.id) {
                    const innerList = await this.expandGroupById(member.id, recipientField, depth + 1);
                    recipientList = recipientList.concat(innerList);
                }
            } else if (member.mail) {
                recipientList.push(member.mail.toLowerCase());
            }
        }
        return recipientList;
    }

    /**
     * Verifies that the email address is RFC822 compliant (not to the full extend though)
     * @param emailAddress
     */
    private checkEmailAddressFormat(emailAddress: string, recipientField?: Office.EmailAddressDetails[]) {
        if (typeof(emailAddress) !== "string" ) {
            const errorMessage = `Bad email address in recipient field`;
            const errorData = {
                recipientField,
                emailAddress
            };
            const error = new Error(errorMessage);
            (error as any).data = errorData;

            throw(error);
        }
        const atSignIndex = emailAddress.lastIndexOf('@');
        if (atSignIndex == -1) {
            throw new GeException('hook.bad_email_address', `Malformed email address: ${emailAddress}`, { address: emailAddress });
        }
    }

    public async resolveAllEmails(): Promise<ResolvedEmails> {
        this._unresolvedLists = [];

        let recipientsByType: RecipientsByAllTypes;
        if (this.itemType === Office.MailboxEnums.ItemType.Appointment) {
            recipientsByType = {
                [AppointMentRecipientType.OPTIONAL]: await this.resolveEmailList(AppointMentRecipientType.OPTIONAL),
                [AppointMentRecipientType.REQUIRED]: await this.resolveEmailList(AppointMentRecipientType.REQUIRED)
            };
        } else {
            recipientsByType = {
                [RecipientType.TO]: await this.resolveEmailList(RecipientType.TO),
                [RecipientType.CC]: await this.resolveEmailList(RecipientType.CC),
                [RecipientType.BCC]: await this.resolveEmailList(RecipientType.BCC)
            };
        }

        return {
            recipientsByType,
            unresolvedLists: [...new Set(this._unresolvedLists)]
        };
    }
}

export const delay = (ms: number) : Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Raised by withTimeout when the wrapped promise does not settle in time. Carries the operation name
 * so the failing call is identifiable from APM without a source map.
 */
export class TimeoutError extends Error {
    public readonly operation: string;
    public readonly timeoutMs: number;

    constructor(operation: string, timeoutMs: number) {
        super(`${operation} timed out after ${timeoutMs}ms`);
        this.name = 'TimeoutError';
        this.operation = operation;
        this.timeoutMs = timeoutMs;
    }
}

/**
 * Records that a bounded call settled after we had already reported it as timed out.
 *
 * Office.js exposes no cancellation API for setAsync/saveAsync/getAsync, so the losing side of the race
 * in withTimeout keeps running to completion. For a mutating call that means the write can still land
 * after we have moved on, leaving our in-memory state and the actual item out of step - saveDraft is
 * the case that matters, since a late save leaves `emailId` unset while the draft does exist.
 *
 * That cannot be prevented, so it is made observable instead: a steady rate here means a budget is too
 * tight, while silence means timeouts really are wedged calls rather than slow ones. Errors are
 * reported outside a transaction, which APM RUM ships (transaction.name is simply absent).
 */
const reportLateSettlement = (operation: string, timeoutMs: number, outcome: 'resolved' | 'rejected') => {
    try {
        OutlookActions.apm?.captureError(
            new Error(`${operation} settled (${outcome}) after its ${timeoutMs}ms timeout was already reported`)
        );
    } catch {
        // diagnostics must never break the send path
    }
};

/**
 * Bounds a promise that may never settle. Office.js host callbacks can fail to fire at all - when that
 * happens inside a synchronous ItemSend handler the send wedges permanently (AB#14110), so every such
 * call on the send path must go through here.
 *
 * This races rather than cancels, because Office.js gives us nothing to cancel with. The underlying
 * call keeps running and its result is discarded; if it later settles, reportLateSettlement records it.
 * Callers that mutate state must therefore treat a timeout as "unknown outcome", not as "did not
 * happen" - the write may still land. HTTP callers should prefer axios's own `timeout`, which does
 * genuinely abort the request.
 */
export const withTimeout = <T>(promise: Promise<T>, ms: number, operation = 'Operation'): Promise<T> => {
    let timer: ReturnType<typeof setTimeout>;
    let timedOut = false;

    const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
            timedOut = true;
            reject(new TimeoutError(operation, ms));
        }, ms);
    });

    // Observe the loser of the race. Both branches are handled so this never surfaces as an unhandled
    // rejection, and both are no-ops unless the timeout has already fired.
    promise.then(
        () => { if (timedOut) reportLateSettlement(operation, ms, 'resolved'); },
        () => { if (timedOut) reportLateSettlement(operation, ms, 'rejected'); },
    );

    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer)) as Promise<T>;
}

/**
 * withTimeout for calls that must not propagate a timeout to the caller - either because their own
 * failure mode is to resolve a fallback rather than reject, or because they sit on a recovery path
 * where throwing would replace the original error.
 *
 * The timeout is swallowed for control-flow purposes but always reported: these are the cases where
 * the add-in carries on having skipped something it intended to do, so the only record that it
 * happened is what we emit here. Never make this silent.
 */
export const withTimeoutOr = async <T>(promise: Promise<T>, ms: number, fallback: T, operation = 'Operation'): Promise<T> => {
    try {
        return await withTimeout(promise, ms, operation);
    } catch (e) {
        if (e instanceof TimeoutError) {
            try {
                OutlookActions.apm?.getCurrentTransaction()?.addLabels({ [`timed-out:${operation}`]: true });
                OutlookActions.apm?.captureError(e);
            } catch {
                // diagnostics must never break the send path
            }

            return fallback;
        }
        throw e;
    }
}

export const AuditEvents = {
    PDP_DECISION: 'outlook_pdp_decision',
    USER_DECISION: 'outlook_pep_user_decision',
    EMAIL_SENT: 'outlook_email_sent',
    CANCEL: 'outlook_pep_prompted_cancel',
}

export const constructAuditEventData = (action: string, geApiState: GeApiState): AuditPayload => {
    const platformInfo = JSON.parse(localStorage.getItem("platformInfo"));
    const platformData: PlatformInfo = {
        type: "outlook",
        outlook: {
            platform: platformInfo?.platform,
            version: platformInfo?.version
        }
    }

    const mapRecipientToPayload = (recipient: AuditEmailRecipient): AuditEmailRecipient => {
        return {
            email: recipient.email,
            is_cc: recipient.is_cc,
            is_bcc: recipient.is_bcc,
        }
    };

    const allRecipients = geApiState.allRecipients?.map(mapRecipientToPayload);
    const allowedRecipients = geApiState.allRecipients
        ?.filter((recipient) => recipient.allowed || recipient?.decision === DECISION_OVERRIDE)
        .map(mapRecipientToPayload);

    let label = null;

    if (geApiState.label?.id) {
        label = {
            id: geApiState.label?.id,
            name: geApiState.label?.name,
            description: geApiState.label?.tooltip,
        };
    }
    const eventData: AuditEvent = {
        id: uuidv4(),
        timestamp: new Date().toISOString(),
        type: action,
        trace_id: geApiState.auditTraceId,
    }

    const actorData: AuditActor = {
        app: {
            id: 'outlook_pep_addin',
            tenant_id: formatToUuid(geApiState.decodedTokenPayload?.tid),
            version: new GlobalConfiguration().getVersion(),
            type: 'eshare',
            organization_key: geApiState.extraServerAuditData?.organization?.key,
        },
        user: {
            email: geApiState.decodedTokenPayload?.smtp || geApiState.decodedTokenPayload?.upn || geApiState.decodedTokenPayload?.preferred_username,
            entra_user: {
                object_id: formatToUuid(geApiState.decodedTokenPayload?.oid),
                upn: geApiState.decodedTokenPayload?.upn,
                puid: geApiState.decodedTokenPayload?.puid,
                tenant_id: formatToUuid(geApiState.decodedTokenPayload?.tid),
            },
            organization_key: geApiState.extraServerAuditData?.organization?.key,
        }
    }

    const emailData: AuditingEmailData = {
        outlook_draft_id: geApiState.emailId,
        subject: geApiState.subject,
        sender: {
            email: geApiState.decodedTokenPayload?.smtp || geApiState.decodedTokenPayload?.upn || geApiState.decodedTokenPayload?.preferred_username,
        },
        // For these events we need allowed-only recipients in the email data.
        recipients: action === AuditEvents.EMAIL_SENT || action === AuditEvents.USER_DECISION
            ? allowedRecipients
            : allRecipients,
        ...(label && { sensitivity_label: label }),
        attachments: geApiState.attachments,
        native_links: geApiState.nativeLinks?.map((link) => {
            return {
                url: link
            }
        })
    }

    let pdpDecisionData: PDPDecision;

    if (action === AuditEvents.PDP_DECISION) {
        pdpDecisionData = {
            outcome: geApiState.allRecipients.map((recipient) => {
                return {
                    email_recipient: {
                        email: recipient.email,
                        is_cc: recipient.is_cc,
                        is_bcc: recipient.is_bcc
                    },
                    reason: recipient.reason,
                    sensitivity_label: label,
                    sensitivity_label_source: "email",
                    allowed: recipient.allowed
                }
            }),
            ...(geApiState.extraServerAuditData?.abac && geApiState.extraServerAuditData?.abac.type && {
                pdp_service: {
                    type: geApiState.extraServerAuditData?.abac?.type,
                    host: geApiState.extraServerAuditData?.abac?.host,
                    endpoint: geApiState.extraServerAuditData?.abac?.endpoint
                }
            })
        }
    }

    let user_decision: UserDecision[] | undefined;
    if (action === AuditEvents.USER_DECISION) {
        user_decision = geApiState.allRecipients && geApiState.allRecipients.length > 0 ? (
            geApiState.allRecipients
                // Filter out recipients without a decision, they are allowed from the beginning.
                .filter((recipient) => recipient.decision)
                .map((recipient) => {
                    return {
                        decision: recipient.decision,
                        recipient: {
                            email: recipient.email,
                            is_cc: recipient.is_cc,
                            is_bcc: recipient.is_bcc,
                        },
                        // If the decision is `override_removal`, we need to include the justification as "reason".
                        ...(recipient.decision === DECISION_OVERRIDE && { reason: recipient.justification })
                    }
            }
        )) : undefined;
    }

    return {
        outlook: {
            platform: platformData,
            event: eventData,
            actor: actorData,
            email: emailData,
            ...(pdpDecisionData && { pdp_decision: pdpDecisionData }),
            ...(user_decision && { outlook_user_decision: user_decision })
        }
    }
}

// check_recipients HTML-escapes email addresses in its response (e.g. "o'malley" -> "o&#x27;malley"),
// so a blocked recipient's email must be decoded before comparing it against the plain address on the
// compose item, otherwise the removal filter silently fails to match and the recipient stays on the send.
export const decodeHtmlEntities = (str: string): string => {
    if (!str) return str;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    return textarea.value;
};

export const formatToUuid = (str: string): string | undefined => {
    if (!str) return undefined;
    const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    if (uuidRegex.test(str)) {
        return str;
    }

    const canBeTransformedToUuidRegex = /^[0-9a-fA-F]{32}$/;
    if (canBeTransformedToUuidRegex.test(str)) {
        return `${str.slice(0, 8)}-${str.slice(8, 12)}-${str.slice(12, 16)}-${str.slice(16, 20)}-${str.slice(20)}`;
    }

    throw new Error("Could not format to UUID");
}

// Output platform in a user-friendly format.
export const getOutlookPlatform = ():string => {
    const platform = Office.context.platform;
    let platformDescription: string;

    switch (platform) {
        case Office.PlatformType.PC:
            platformDescription = "windows";
            break;
        case Office.PlatformType.Mac:
            platformDescription = "mac";
            break;
        case Office.PlatformType.OfficeOnline:
            platformDescription = "web";
            break;
        case Office.PlatformType.iOS:
            platformDescription = "ios";
            break;
        case Office.PlatformType.Android:
            platformDescription = "android";
            break;
        default:
            platformDescription = "unknown";
    }

    return platformDescription
}

export const endAPMValidationModalSpan = (error?: any) => {
    const span = (window as any).__currentModalSpan;
    if (span) {
        console.log('Closing the validation span');
        // End the span that we started before opening the iframe
        if (error) {
            span.addLabels({
                'error': error?.message,
                'error.type': error?.name
            });
        }
        span.end();
        delete (window as any).__currentModalSpan;
    }
}


export class ApiInstanceError extends Error {
    public apiInstance: GeApi;
    public originalError: any;

    constructor(originalError: any, apiInstance: GeApi) {
        super(originalError.message);
        this.apiInstance = apiInstance;
        this.originalError = originalError;
        this.name = 'ApiInstanceError';

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ApiInstanceError);
        }
    }
}

// Checks if a feature is enabled in the i18n configuration.
// All enabled features are stored in the i18n translations under the "features" namespace.
// All features are enabled in development mode by default.
export const featureDetect = (feature: string, t: TFunction, i18n: i18n): boolean => {
    return (i18n.exists(`features.${feature}`) && !!t(`features.${feature}`, {
        returnObjects: true
    }));
}

// Transforms the tenant name to a user-friendly format.
// This is used to display the tenant name in the kibana UI for now.
// TODO: This should be removed when new manifest is released and the tenant names are correct in the manifest deployment url param.
export const transformTenant = (tenant?: string): string => {
    if (!tenant) {
        return 'Unknown';
    }
    if (tenant === 'eshare' || tenant === 'ge_prod_commercial') {
        return 'GE';
    }
    if (tenant === 'eshare_gov_com') {
        return 'eShare';
    }
    if (tenant === 'esg') {
        return 'ESG Demo';
    }

    return tenant;
};